import React from 'react';
import { useForm } from '../../context/FormContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';

const calculateAge = (day, month, year) => {
  if (!day || !month || !year || year.length < 4) return null;
  const dob = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
  return age >= 0 && age < 120 ? age : null;
};

export const DateOfBirthStep = () => {
  const { formData, updateFormData } = useForm();

  // Parse existing date
  const parseDate = () => {
    if (!formData.date_of_birth) return { day: '', month: '', year: '' };
    const parts = formData.date_of_birth.split('/');
    if (parts.length === 3) {
      return { day: parts[0], month: parts[1], year: parts[2] };
    }
    return { day: '', month: '', year: '' };
  };

  const { day, month, year } = parseDate();

  const updateDate = (field, value) => {
    const current = parseDate();
    current[field] = value;
    
    // Only format if we have values
    if (current.day || current.month || current.year) {
      const formatted = `${current.day.padStart(2, '0')}/${current.month.padStart(2, '0')}/${current.year}`;
      updateFormData({ date_of_birth: formatted });
    }
  };

  // Generate day options (1-31)
  const days = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
  
  // Generate month options (1-12)
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  
  // Generate year options (current year - 100 to current year - 18)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 82 }, (_, i) => String(currentYear - 18 - i));

  const age = calculateAge(day, month, year);

  return (
    <div className="space-y-6" data-testid="dob-step">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Are you eligible?
        </h2>
        <p className="text-slate-600 hidden md:block">What is your date of birth?</p>
      </div>

      <div className="max-w-md mx-auto">
        <Label className="text-sm font-medium text-slate-700 mb-3 block">
          Date of Birth <span className="text-red-500">*</span>
        </Label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Day</Label>
            <Select value={day} onValueChange={(val) => updateDate('day', val)}>
              <SelectTrigger className="h-12" data-testid="dob-day">
                <SelectValue placeholder="Day" />
              </SelectTrigger>
              <SelectContent>
                {days.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Month</Label>
            <Select value={month} onValueChange={(val) => updateDate('month', val)}>
              <SelectTrigger className="h-12" data-testid="dob-month">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {months.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Year</Label>
            <Select value={year} onValueChange={(val) => updateDate('year', val)}>
              <SelectTrigger className="h-12" data-testid="dob-year">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {age !== null && (
          <p className="text-center mt-4" data-testid="dob-age">
            <span className="text-sm text-slate-500">Age: </span>
            <span className="text-lg font-semibold text-slate-800">{age}</span>
          </p>
        )}
        {age !== null && age > 90 && (
          <p
            className="mt-3 text-center text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
            data-testid="dob-age-high-warning"
          >
            Applicant age is {age} — please double-check the date of birth is correct.
          </p>
        )}
        {age !== null && age < 18 && (
          <p
            className="mt-3 text-center text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            data-testid="dob-age-low-warning"
          >
            Applicant must be 18 or over (currently {age}).
          </p>
        )}
        <p className="text-sm text-slate-500 mt-3 text-center">
          You must be 18 or over to be eligible
        </p>
      </div>
    </div>
  );
};

export default DateOfBirthStep;
